import { useNavigate } from "@tanstack/react-router"
import { useCallback } from "react"
import { useMutation } from "urql"

import type { SoftReceivedPurchaseOrdersFormValues } from "../../../schemas"
import { calculateSoftReceivedPurchaseOrderFormTotals } from "../../../utils"
import { validateFinalizeCheckIn } from "../utils"

import { useSoftReceivedLotsValidation } from "./useSoftReceivedLotsValidation"

import type { GoodsReceipt, GoodsReceiptPalletRow, Order } from "@/graphql/codegen/graphql"
import { GoodsReceiptStatus, ProcessGoodsReceiptDocument } from "@/graphql/codegen/graphql"
import { BasicPathType, useAppView } from "@/hooks/useAppView"
import { NotificationType } from "@/schemas/notification"
import { useGoodsReceiptActions } from "@/screens/WarehouseReceiving/hooks/useGoodsReceiptActions"
import { GoodReceiptActionType } from "@/screens/WarehouseReceiving/types"
import type { GoodsReceiptActionState } from "@/screens/WarehouseReceiving/types"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import type { ActionOutcome } from "@/types/common"

interface UseSoftReceivedGoodsReceivedActionsProps {
  goodsReceipt?: GoodsReceipt | null
  selectedPurchaseOrders: Order[]
  addPurchaseOrderSuccessMessage?: string
  formValues: SoftReceivedPurchaseOrdersFormValues
  hasUnsavedChanges: boolean
  onRefetch: () => void
  onSave: () => Promise<ActionOutcome<GoodsReceiptPalletRow[]>>
}

interface UseSoftReceivedGoodsReceivedActionsReturn {
  actionState: GoodsReceiptActionState
  purchaseOrderToRemove: number | null
  handleDialogClose: () => void
  handleEditGoodsReceipt: (goodsReceipt: GoodsReceipt) => void
  handleMarkAsNotReceived: (goodsReceipt: GoodsReceipt) => void
  handleUpload: () => void
  handleAddPurchaseOrder: () => void
  handleConfirmAddPurchaseOrder: () => Promise<void>
  handleRemovePurchaseOrder: (purchaseOrderId: number) => void
  handlePurchaseOrderRemoveClose: () => void
  handlePurchaseOrderRemoveSuccess: () => void
  handleFinalizeCheckIn: () => Promise<void>
  handleProceedToFinalizeCheckIn: () => Promise<void>
  handleConfirmUntestedLots: () => void
}

export const useSoftReceivedGoodsReceivedActions = ({
  goodsReceipt,
  selectedPurchaseOrders,
  addPurchaseOrderSuccessMessage,
  formValues,
  hasUnsavedChanges,
  onRefetch,
  onSave,
}: UseSoftReceivedGoodsReceivedActionsProps): UseSoftReceivedGoodsReceivedActionsReturn => {
  const navigate = useNavigate()
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const { getBasicPathFor } = useAppView()
  const { validateLotsTested } = useSoftReceivedLotsValidation()
  const [, executeProcessMutation] = useMutation(ProcessGoodsReceiptDocument)

  const {
    actionState,
    purchaseOrderToRemove,
    handleDialogClose,
    handleEditGoodsReceipt,
    handleAddPurchaseOrder,
    handleConfirmAddPurchaseOrder,
    handleRemovePurchaseOrder,
    handlePurchaseOrderRemoveClose,
    handlePurchaseOrderRemoveSuccess,
    handleUploadFiles,
    setActionState,
  } = useGoodsReceiptActions({
    goodsReceipt,
    selectedPurchaseOrders,
    onRefetch,
    addPurchaseOrderSuccessMessage,
  })

  const handleMarkAsNotReceived = useCallback(
    (goodsReceipt: GoodsReceipt) => {
      setActionState({ type: GoodReceiptActionType.MarkAsNotReceived, goodsReceipt })
    },
    [setActionState]
  )

  const handleProceedToFinalizeCheckIn = useCallback(async () => {
    if (!goodsReceipt) return

    handleDialogClose()

    const result = await executeProcessMutation({
      input: {
        goodsReceiptId: goodsReceipt.goodsReceiptId,
        status: GoodsReceiptStatus.PreCheckedIn,
      },
    })

    if (result.error) {
      notify({
        type: NotificationType.Error,
        silent: false,
        message: result.error.message || "Failed to update goods receipt status.",
      })
      return
    }

    const data = result.data?.goodsReceipt.processReceipt

    if (data?.__typename === "GoodsReceiptUpdateFailure") {
      notify({
        type: NotificationType.Error,
        silent: false,
        message: data.error || "Failed to update goods receipt status.",
      })
      return
    }

    navigate({
      to: `${getBasicPathFor(BasicPathType.WAREHOUSE_RECEIVING)}/soft-received/$goodsReceiptId/finalize-check-in`,
      params: { goodsReceiptId: goodsReceipt.goodsReceiptId },
    })
  }, [navigate, goodsReceipt, getBasicPathFor, handleDialogClose, executeProcessMutation, notify])

  const handleConfirmUntestedLots = useCallback(() => {
    handleDialogClose()

    const formPurchaseOrderLines = formValues.purchaseOrders.flatMap((po) => po.lines)

    const softReceivedPalletsCount = goodsReceipt?.softReceiveData?.deliveredPalletsCount ?? 0
    const totals = calculateSoftReceivedPurchaseOrderFormTotals(formPurchaseOrderLines)

    if (softReceivedPalletsCount === totals.palletsCount) {
      handleProceedToFinalizeCheckIn()
    } else {
      setActionState({
        type: GoodReceiptActionType.FinalizeCheckInConfirmPalletsCount,
      })
    }
  }, [handleDialogClose, goodsReceipt, formValues, setActionState, handleProceedToFinalizeCheckIn])

  const handleFinalizeCheckIn = useCallback(async () => {
    const validationResult = validateFinalizeCheckIn(formValues)

    if (!validationResult.isValid) {
      validationResult.errors.forEach((error) => {
        notify({
          type: NotificationType.Error,
          message: error,
        })
      })
      return
    }

    if (hasUnsavedChanges && onSave) {
      const result = await onSave()

      if (result.error) {
        return
      }
    }

    const untestedLots = await validateLotsTested(formValues, goodsReceipt!)

    if (untestedLots.length > 0) {
      setActionState({
        type: GoodReceiptActionType.FinalizeCheckInUntestedLots,
        untestedLots,
      })
    } else {
      handleConfirmUntestedLots()
    }
  }, [
    formValues,
    hasUnsavedChanges,
    goodsReceipt,
    onSave,
    notify,
    validateLotsTested,
    setActionState,
    handleConfirmUntestedLots,
  ])

  const handleUpload = handleUploadFiles

  return {
    actionState,
    purchaseOrderToRemove,
    handleDialogClose,
    handleEditGoodsReceipt,
    handleMarkAsNotReceived,
    handleUpload,
    handleAddPurchaseOrder,
    handleConfirmAddPurchaseOrder,
    handleRemovePurchaseOrder,
    handlePurchaseOrderRemoveClose,
    handlePurchaseOrderRemoveSuccess,
    handleFinalizeCheckIn,
    handleProceedToFinalizeCheckIn,
    handleConfirmUntestedLots,
  }
}
